import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import B2BLayout from '../../../layouts/b2b'
import { Form, Result, Space } from 'antd'
import { PlusOutlined, FilterOutlined } from '@ant-design/icons'
import { getColumns } from './columns'
import fetchAPI, { IHttpError } from '../../../../lib/utils/fetch-api'
import { useNavigate, useLocation } from 'react-router-dom'
import FiltersList from '../../../components/v2/filters'
import { objectToUrl } from '../../../pages_v2/utils'
import {
  currentFiltersProps,
  EmployeeListItem,
  Enums,
  TablePaginationPosition,
  ISorter,
} from './interfaces'
import { createSelector } from 'reselect'
import { useSelector } from 'react-redux'
import {
  getJsonSettingsByKey,
  getColumnOrder,
  TableSettingsButton,
  TransferItem,
} from '../../../components/v2/table-settings-button'
import FiltersDrawer, { FiltersDrawerRef } from '../../../components/v2/filters-drawer'
import { getFilters } from './filters'
import DataTable from '../../../components/data-table'
import { useHeaderActions } from '../../../contexts/header-actions-context'
import { HeaderAction } from '../../../contexts/header-actions-context/interface'
import { getMobileFields } from './mobile-fields'
import { ResultStatusType } from 'antd/lib/result'
import StickyToolbar from '../../../layouts/b2b/sticky-toolbar'
import { getPrintPath } from '../../../components/v2/export-excel-button'
import { EmployeePrintButton } from './ui/employee-print-button/employee-print-button'

const createMenuCounters = createSelector(
  (state: any) => state.menuV2,
  counters => counters,
)

function parseUrlSearchParams(search: string): currentFiltersProps {
  const urlParams = new URLSearchParams(search)
  return {
    id: Number(urlParams.get('id')) || undefined,
    fio: urlParams.get('fio') || undefined,
    phone: urlParams.get('phone') || undefined,
    snils: urlParams.get('snils') || undefined,
    post_id: Number(urlParams.get('post_id')) || undefined,
    department_id: Number(urlParams.get('department_id')) || undefined,
    office_id: Number(urlParams.get('office_id')) || undefined,
    region_id: Number(urlParams.get('region_id')) || undefined,
    company_id: Number(urlParams.get('company_id')) || undefined,
    is_active: urlParams.get('is_active') || undefined,
    status: (urlParams.get('status') as 'active' | 'dismissed' | 'maternity') || undefined,
    employment_date: urlParams.get('employment_date') || undefined,
    dismissal_date: urlParams.get('dismissal_date') || undefined,
  }
}

const EmployeesList = () => {
  const [filtersForm] = Form.useForm()
  const initialPageSize = (() => {
    const savedPageSize = localStorage.getItem('employeesPageSize')
    const parsedPageSize = Number(savedPageSize)
    return parsedPageSize && !isNaN(parsedPageSize) && parsedPageSize <= 150 ? parsedPageSize : 50
  })()

  const headerRef = useRef<HTMLDivElement | null>(null)
  const filtersDrawerRef = useRef<FiltersDrawerRef | null>(null)
  const tableSettingsRef = useRef<{ open: () => void } | null>(null)
  const isInitialLoadRef = useRef(true)
  const isLoadingFiltersRef = useRef(false)
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const [data, setData] = useState<EmployeeListItem[]>([])
  const [availableFilters, setAvailableFilters] = useState<Enums>({
    post: [],
    department: [],
    office: [],
    region: [],
    city: [],
    company: [],
  })
  const [urlParams] = useState(Object.fromEntries(new URLSearchParams(search)))
  const [currentFilters, setCurrentFilters] = useState<currentFiltersProps>({})
  const { setHeaderActions } = useHeaderActions()

  const [loading, setLoading] = useState(false)
  const [bottomCenter] = useState<TablePaginationPosition>('bottomCenter')
  const [sorter, setSorter] = useState<ISorter>({})
  const [pagination, setPagination] = useState({
    current: Number(urlParams?.page) || 1,
    pageSize: initialPageSize,
    position: [bottomCenter],
    showSizeChanger: true,
    total: 0,
  })
  const { counters } = useSelector(createMenuCounters)
  const [localCounter, setLocalCounter] = useState<null | number>(null)
  const jsonSettings = useSelector(state => getJsonSettingsByKey(state, pathname))

  interface IError {
    code?: ResultStatusType
    message?: string
  }
  const [error, setError] = useState<null | IError>(null)

  const columns = useMemo(() => getColumns(sorter), [sorter])

  const viewColumns = useMemo(() => {
    const enhanceColumn = (column: any) => {
      if (!('dataIndex' in column) || !column.editable) {
        return column
      }
      return {
        ...column,
      }
    }

    const columnOrder = getColumnOrder(jsonSettings)

    if (columnOrder.length > 0) {
      const enhancedColumns = columns.map(enhanceColumn)
      const result: any[] = []
      for (let i = 0; i < columnOrder.length; i++) {
        const item = enhancedColumns.find(column => columnOrder[i] === column.dataIndex)
        if (item) {
          result.push(item)
        }
      }

      const actionColumn = enhancedColumns.find(column => column.key === 'operation')
      if (actionColumn && !result.find(col => col.key === 'operation')) {
        result.push(actionColumn)
      }

      return result
    }

    return columns.map(enhanceColumn)
  }, [columns, jsonSettings])

  const mobileFields = useMemo(() => getMobileFields(), [])

  const settingsColumns = useMemo(
    () =>
      columns.map(column => ({
        key: (column as any)?.dataIndex,
        title: column?.title,
      })),
    [columns],
  ) as TransferItem[]

  const excelPath = useMemo(() => {
    return getPrintPath('/api/hr/employees', new URLSearchParams(search))
  }, [search])

  useEffect(() => {
    const newFilters = parseUrlSearchParams(search)
    setCurrentFilters(prevState => ({ ...prevState, ...newFilters }))
  }, [search])

  useEffect(() => {
    const newCounterData = counters?.['/hr-employees']
    if (newCounterData && !localCounter) {
      setLocalCounter(newCounterData)
    }
    if (newCounterData && localCounter && newCounterData !== localCounter) {
      setLocalCounter(newCounterData)
      getData()
    }
  }, [counters])

  useEffect(() => {
    if (isInitialLoadRef.current) {
      getAvailableFilters()
      isInitialLoadRef.current = false
    }
  }, [])

  useEffect(() => {
    getData()
  }, [search])

  const deleteRecord = useCallback(
    currentId => {
      fetchAPI(`/api/hr/employees/${currentId}`, { method: 'DELETE' })
        .then(() => {
          const urlParams = Object.fromEntries(new URLSearchParams(search))
          fetchAPI(`/api/hr/employees${objectToUrl(urlParams)}`)
            .then(({ data, pagination: paginationData, sorter: sorterData }) => {
              if (data) {
                setData(data)
                setPagination(prevState => ({
                  ...prevState,
                  ...paginationData,
                }))
                if (sorterData) {
                  setSorter(sorterData)
                }
              }
            })
            .catch(() => {
              console.error('Не удалось обновить список после удаления')
            })
        })
        .catch(() => {
          console.error('Не удалось удалить запись')
        })
    },
    [search],
  )

  const getAvailableFilters = useCallback(() => {
    if (isLoadingFiltersRef.current) {
      return
    }
    isLoadingFiltersRef.current = true
    fetchAPI('/api/hr/employees/enum')
      .then(data => {
        if (data?.enum) {
          setAvailableFilters({
            post: data.enum.hr_post || [],
            department: data.enum.hr_department || [],
            office: data.enum.office || [],
            region: data.enum.hr_region_id || [],
            city: data.enum.hr_city_id || [],
            company: data.enum.company || [],
          })
        }
        isLoadingFiltersRef.current = false
      })
      .catch(e => {
        console.error('Не удалось получить список доступных фильтров', e)
        isLoadingFiltersRef.current = false
      })
  }, [])

  const getData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const urlParams = Object.fromEntries(new URLSearchParams(search))
      await fetchAPI(`/api/hr/employees${objectToUrl(urlParams)}`).then(
        ({ data, pagination: paginationData, sorter: sorterData }) => {
          if (data) {
            setData(data)
            setPagination(prevState => ({
              ...prevState,
              ...paginationData,
            }))
            if (sorterData) {
              setSorter(sorterData)
            }
          }
        },
      )
    } catch (error) {
      console.error('Ошибка при получении данных:', error)
      setError({
        message: (error as IHttpError)?.message || 'Неизвестная ошибка',
        code: ((error as IHttpError)?.statusCode || 'error') as ResultStatusType,
      })
    } finally {
      setLoading(false)
    }
  }, [search])

  const handleTableChange = useCallback(
    (_pagination, _filters, sorter) => {
      const currentUrlParams = Object.fromEntries(new URLSearchParams(search))
      setPagination(prevState => ({ ...prevState, ..._pagination }))
      const current: Record<string, any> = {
        ...currentUrlParams,
        sort_order: sorter?.order,
        sort_field: sorter?.field,
        page: _pagination.current,
        pageSize: _pagination.pageSize,
      }
      if (current?.page === 1) {
        delete current.page
      }
      if (current?.pageSize === 10) {
        delete current.pageSize
      }
      if (current?.sort_order === undefined) {
        delete current.sort_order
        delete current.sort_field
      }
      // Удаляем все параметры со значением undefined
      Object.keys(current).forEach(key => {
        if (current[key] === undefined || current[key] === null) {
          delete current[key]
        }
      })
      const urlParams = new URLSearchParams(current).toString()
      navigate({ search: urlParams })
    },
    [search, navigate],
  )

  const handlePageChange = (page: number, pageSize: number) => {
    setPagination(prevState => ({
      ...prevState,
      current: Number(page) || 1,
      pageSize: pageSize > 150 ? 150 : pageSize,
    }))

    if (!isNaN(pageSize)) {
      localStorage.setItem('employeesPageSize', String(pageSize))
    }

    const currentUrlParams = new URLSearchParams(search)
    if (page && page !== 1) {
      currentUrlParams.set('page', page.toString())
    } else {
      currentUrlParams.delete('page')
    }
    if (!isNaN(pageSize)) {
      if (pageSize && pageSize !== 10) {
        currentUrlParams.set('pageSize', pageSize.toString())
      } else {
        currentUrlParams.delete('pageSize')
      }
    }
    navigate({ search: currentUrlParams.toString() })
  }

  const handleRowClick = record => {
    const { id } = record
    navigate(`/hr-employees/${id}`)
  }

  const changeFiltersHandler = useCallback(
    filters => {
      const tmpFilter = { ...(filters || {}), pageSize: pagination.pageSize }
      // Удаляем все параметры со значением undefined или null
      Object.keys(tmpFilter).forEach(key => {
        if (tmpFilter[key] === undefined || tmpFilter[key] === null) {
          delete tmpFilter[key]
        }
      })
      const newUrlParams = new URLSearchParams(tmpFilter).toString()
      navigate({ search: newUrlParams })
    },
    [pagination, navigate],
  )

  const filters = useMemo(() => {
    return getFilters({ currentFilters, availableFilters })
  }, [availableFilters, currentFilters])

  useEffect(() => {
    const actions: HeaderAction[] = [
      {
        key: 'create',
        icon: <PlusOutlined />,
        onClick: () => {
          navigate('/hr-employees/new')
        },
        tooltip: 'Создать сотрудника',
      },
      {
        key: 'filters',
        icon: <FilterOutlined />,
        onClick: () => {
          filtersDrawerRef.current?.toggle()
        },
        tooltip: 'Фильтры',
      },
    ]

    setHeaderActions(actions)

    return () => {
      setHeaderActions([])
    }
  }, [setHeaderActions, navigate])

  const dataWithActions = useMemo(() => {
    return data.map(item => ({
      ...item,
      deleteRecord,
    }))
  }, [data, deleteRecord])

  return (
    <B2BLayout pageTitle={'Сотрудники'} isViewScrollButton>
      {error && <Result status={error.code} title="Ошибка" subTitle={error.message} />}
      {!error && (
        <>
          <StickyToolbar
            ref={headerRef}
            collapsible={
              <Space
                style={{ width: '100%', justifyContent: 'space-between' }}
                align={'start'}
                wrap
              >
                <Space align={'start'} wrap>
                  <FiltersList
                    filters={filters.filter(filter => ['fio', 'phone'].includes(filter.name))}
                    onChange={changeFiltersHandler}
                    form={filtersForm}
                  />
                </Space>
                <FiltersDrawer
                  filters={filters.filter(filter => !['fio', 'phone'].includes(filter.name))}
                  onChange={changeFiltersHandler}
                  currentFilters={Object.fromEntries(
                    Object.entries(currentFilters).filter(
                      ([key]) => !['fio', 'phone'].includes(key),
                    ),
                  )}
                  ref={filtersDrawerRef}
                />
              </Space>
            }
            persistent={
              <Space style={{ width: '100%', justifyContent: 'end' }} align={'end'} wrap>
                <Space align={'center'} wrap>
                  <EmployeePrintButton path={excelPath} columns={viewColumns} />
                  <TableSettingsButton items={settingsColumns} ref={tableSettingsRef} />
                </Space>
              </Space>
            }
          />
          <DataTable
            columns={viewColumns}
            mobileFields={mobileFields}
            data={dataWithActions}
            loading={loading}
            rowKey={r => String(r.id)}
            highlightLastRowPrefix={'employee'}
            pagination={pagination}
            onRowClick={handleRowClick}
            onPageChange={handlePageChange}
            onTableChange={handleTableChange}
            className={'employees-table'}
            headerRef={headerRef}
            maxLines={1}
            bordered
            scroll={{ x: 'max-content' }}
          />
        </>
      )}
    </B2BLayout>
  )
}

export default EmployeesList
